import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';

const AppContext = createContext();

const ADMINS = [
  { id: 'admin1', username: 'mehedi', password: 'admin123', role: 'admin', name: 'Mehedi' },
  { id: 'admin2', username: 'admin2', password: 'admin456', role: 'admin', name: 'Admin 2' },
];

export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('mpos_user');
    return saved ? JSON.parse(saved) : null;
  });

  const [artists, setArtists] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [cones, setCones] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentUser) localStorage.setItem('mpos_user', JSON.stringify(currentUser));
    else localStorage.removeItem('mpos_user');
  }, [currentUser]);

  // Real-time listeners — wait until all 4 have fired at least once
  useEffect(() => {
    let loaded = { artists: false, jobs: false, cones: false, expenses: false };

    const checkReady = () => {
      if (loaded.artists && loaded.jobs && loaded.cones && loaded.expenses) {
        setLoading(false);
      }
    };

    const u1 = onSnapshot(collection(db, 'artists'), (snap) => {
      setArtists(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      loaded.artists = true;
      checkReady();
    });

    const u2 = onSnapshot(collection(db, 'jobs'), (snap) => {
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      loaded.jobs = true;
      checkReady();
    });

    const u3 = onSnapshot(collection(db, 'cones'), (snap) => {
      setCones(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      loaded.cones = true;
      checkReady();
    });

    const u4 = onSnapshot(collection(db, 'expenses'), (snap) => {
      setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      loaded.expenses = true;
      checkReady();
    });

    // Fallback if Firebase is slow
    const timeout = setTimeout(() => setLoading(false), 6000);

    return () => { u1(); u2(); u3(); u4(); clearTimeout(timeout); };
  }, []);

  // Keep logged-in artist in sync
  useEffect(() => {
    if (currentUser && currentUser.role === 'artist') {
      const updated = artists.find(a => a.id === currentUser.id);
      if (updated) setCurrentUser(prev => ({ ...prev, ...updated, role: 'artist' }));
    }
  }, [artists]);

  const login = (username, password) => {
    const admin = ADMINS.find(a => a.username === username && a.password === password);
    if (admin) { setCurrentUser(admin); return { success: true, role: 'admin' }; }
    const artist = artists.find(a => a.name.toLowerCase() === username.toLowerCase() && a.pin === password);
    if (artist) { setCurrentUser({ ...artist, role: 'artist' }); return { success: true, role: 'artist' }; }
    return { success: false };
  };

  const logout = () => setCurrentUser(null);

  const addArtist = async (artist) => {
    const id = `a_${Date.now()}`;
    const newArtist = { ...artist, id, present: false, active: false, checkInTime: null, totalWorkMinutes: 0 };
    await setDoc(doc(db, 'artists', id), newArtist);
  };

  const updateArtist = async (id, updates) => {
    await updateDoc(doc(db, 'artists', id), updates);
  };

  const removeArtist = async (id) => {
    await deleteDoc(doc(db, 'artists', id));
  };

  const togglePresent = async (id) => {
    const a = artists.find(x => x.id === id);
    if (!a) return;
    const nowPresent = !a.present;
    const updates = { present: nowPresent };
    if (!nowPresent) { updates.active = false; updates.checkInTime = null; }
    await updateDoc(doc(db, 'artists', id), updates);
  };

  const checkIn = async (id) => {
    await updateDoc(doc(db, 'artists', id), { active: true, checkInTime: new Date().toISOString() });
  };

  const checkOut = async (id) => {
    const a = artists.find(x => x.id === id);
    if (!a || !a.checkInTime) return;
    const mins = Math.round((new Date() - new Date(a.checkInTime)) / 60000);
    await updateDoc(doc(db, 'artists', id), { active: false, checkInTime: null, totalWorkMinutes: (a.totalWorkMinutes || 0) + mins });
  };

  const addJob = async (job) => {
    const artist = artists.find(a => a.id === job.artistId);
    if (!artist) return;
    const artistEarning = (job.amount * artist.commission) / 100;
    const id = `j_${Date.now()}`;
    await setDoc(doc(db, 'jobs', id), {
      ...job, id, artistEarning, commission: artist.commission,
      date: job.date || new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString()
    });
  };

  const updateJob = async (id, updates) => {
    const j = jobs.find(x => x.id === id);
    if (!j) return;
    const artist = artists.find(a => a.id === j.artistId);
    const newAmount = updates.amount !== undefined ? updates.amount : j.amount;
    const artistEarning = artist ? (newAmount * artist.commission) / 100 : j.artistEarning;
    await updateDoc(doc(db, 'jobs', id), { ...updates, amount: newAmount, artistEarning, commission: artist ? artist.commission : j.commission });
  };

  const deleteJob = async (id) => { await deleteDoc(doc(db, 'jobs', id)); };

  const addCone = async (cone) => {
    const id = `c_${Date.now()}`;
    await setDoc(doc(db, 'cones', id), {
      ...cone, id, date: cone.date || new Date().toISOString().split('T')[0], createdAt: new Date().toISOString()
    });
  };

  const deleteCone = async (id) => { await deleteDoc(doc(db, 'cones', id)); };

  const addExpense = async (expense) => {
    const id = `e_${Date.now()}`;
    await setDoc(doc(db, 'expenses', id), {
      ...expense, id, date: expense.date || new Date().toISOString().split('T')[0], createdAt: new Date().toISOString()
    });
  };

  const deleteExpense = async (id) => { await deleteDoc(doc(db, 'expenses', id)); };

  const getArtistStats = (artistId, dateFrom, dateTo) => {
    let filtered = jobs.filter(j => j.artistId === artistId);
    if (dateFrom) filtered = filtered.filter(j => j.date >= dateFrom);
    if (dateTo) filtered = filtered.filter(j => j.date <= dateTo);
    const totalRevenue = filtered.reduce((s, j) => s + j.amount, 0);
    const totalEarning = filtered.reduce((s, j) => s + j.artistEarning, 0);
    let filteredCones = cones.filter(c => c.organic && c.artistId === artistId);
    if (dateFrom) filteredCones = filteredCones.filter(c => c.date >= dateFrom);
    if (dateTo) filteredCones = filteredCones.filter(c => c.date <= dateTo);
    return { jobs: filtered, totalRevenue, totalEarning, jobCount: filtered.length, organicConesUsed: filteredCones.reduce((s, c) => s + c.qty, 0), organicConeRevenue: filteredCones.reduce((s, c) => s + (c.qty * c.price), 0) };
  };

  const getSummary = (dateFrom, dateTo) => {
    let filtJobs = jobs, filtCones = cones, filtExp = expenses;
    if (dateFrom) { filtJobs = filtJobs.filter(j => j.date >= dateFrom); filtCones = filtCones.filter(c => c.date >= dateFrom); filtExp = filtExp.filter(e => e.date >= dateFrom); }
    if (dateTo) { filtJobs = filtJobs.filter(j => j.date <= dateTo); filtCones = filtCones.filter(c => c.date <= dateTo); filtExp = filtExp.filter(e => e.date <= dateTo); }
    const totalRevenue = filtJobs.reduce((s, j) => s + j.amount, 0);
    const totalArtistPay = filtJobs.reduce((s, j) => s + j.artistEarning, 0);
    const regularCones = filtCones.filter(c => !c.organic);
    const organicCones = filtCones.filter(c => c.organic);
    const coneRevenue = regularCones.reduce((s, c) => s + (c.price * c.qty), 0);
    const organicConeRevenue = organicCones.reduce((s, c) => s + (c.price * c.qty), 0);
    const totalExpenses = filtExp.reduce((s, e) => s + e.amount, 0);
    const grossIncome = totalRevenue + coneRevenue + organicConeRevenue;
    const profit = grossIncome - totalArtistPay - totalExpenses;
    return { totalRevenue, totalArtistPay, coneRevenue, organicConeRevenue, totalExpenses, grossIncome, profit, jobCount: filtJobs.length, coneCount: regularCones.reduce((s, c) => s + c.qty, 0), organicConeCount: organicCones.reduce((s, c) => s + c.qty, 0) };
  };

  return (
    <AppContext.Provider value={{
      currentUser, login, logout, tick, loading,
      artists, addArtist, updateArtist, removeArtist, togglePresent, checkIn, checkOut,
      jobs, addJob, updateJob, deleteJob,
      cones, addCone, deleteCone,
      expenses, addExpense, deleteExpense,
      getArtistStats, getSummary,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
